/**
 * Typed client for Auteur Bridge release metadata and download grants.
 *
 * Campaign `bridge-download-pairing-v3-20261015`, task BO05 (BO-1, BO-2, BO-5, BO-12, BO-15, BO-16).
 * Reuses existing requestJson and responseRecord from api/http.ts.
 */

import { ApiClientError, requestJson, responseRecord } from './http';

export { ApiClientError as BridgeReleaseApiError };

export type BridgeReleasePlatform = "windows" | "macos";

export interface BridgeRelease {
  version: string;
  file_name: string;
  sha256: string;
  size: number;
  channel: string;
  code_signing_label: string;
  released_at?: string;
  min_supported?: string;
  [key: string]: unknown;
}

export interface BridgeDownloadGrant {
  url: string;
  expires_in: number;
  version: string;
  sha256: string;
  size: number;
  file_name: string;
  [key: string]: unknown;
}

/**
 * Fetch the latest verified release manifest for Auteur Bridge.
 * Entitlement-gated: returns 403 when the local_bridge plan feature is off (BO-1, BO-3).
 */
export async function fetchLatestBridgeRelease(platform: BridgeReleasePlatform = "windows"): Promise<BridgeRelease> {
  const path = platform === "macos" ? "/v1/bridge/releases/macos/latest" : "/v1/bridge/releases/latest";
  const payload = await requestJson(path, {
    method: 'GET',
  });
  const record = responseRecord(payload);
  const target = (typeof record.release === 'object' && record.release !== null && !Array.isArray(record.release))
    ? (record.release as Record<string, unknown>)
    : record;
  const manifest = (typeof target.manifest === 'object' && target.manifest !== null && !Array.isArray(target.manifest))
    ? (target.manifest as Record<string, unknown>)
    : {};
  const releasedAt = target.released_at ?? manifest.released_at;
  const minSupported = target.min_supported ?? manifest.min_supported;
  return {
    version: String(target.version || target.release_version || manifest.version || ''),
    file_name: String(target.file_name || ''),
    sha256: String(target.sha256 || ''),
    size: Number(target.size || 0),
    channel: String(target.channel || ''),
    code_signing_label: String(target.code_signing_label || ''),
    released_at: typeof releasedAt === 'string' ? releasedAt : undefined,
    min_supported: typeof minSupported === 'string' ? minSupported : undefined,
  };
}

/**
 * Request a short-lived presigned download grant for the latest bridge installer.
 * Entitlement-gated, closed body {}, URL is short-lived and never logged (BO-1, BO-2, BO-15).
 */
export async function requestBridgeDownloadGrant(platform: BridgeReleasePlatform = "windows"): Promise<BridgeDownloadGrant> {
  const path = platform === "macos" ? "/v1/bridge/releases/macos/download-grant" : "/v1/bridge/releases/download-grant";
  const payload = await requestJson(path, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  const record = responseRecord(payload);
  const target = (typeof record.grant === 'object' && record.grant !== null && !Array.isArray(record.grant))
    ? (record.grant as Record<string, unknown>)
    : record;
  return {
    url: String(target.url || ''),
    expires_in: Number(target.expires_in || 0),
    version: String(target.version || ''),
    sha256: String(target.sha256 || ''),
    size: Number(target.size || 0),
    file_name: String(target.file_name || ''),
  };
}
